// tslint:disable:variable-name
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { PaginatorState } from '../models/paginator.model';
import { ITableState, TableResponseModel } from '../models/table.model';
import { BaseModel } from '../models/base.model';
import { SortState } from '../models/sort.model';
import { GroupingState } from '../models/grouping.model';
import { environment } from '../../../../../environments/environment';
import { AuthModel } from '../../../../modules/auth/_models/auth.model';
import { AuthService } from '../../../../modules/auth/_services/auth.service';
import { RecursosService } from 'src/app/_services/recursos.service';

const DEFAULT_STATE: ITableState = {
  filter: {},
  paginator: new PaginatorState(),
  sorting: new SortState(),
  searchTerm: '',
  grouping: new GroupingState(),
  entityId: undefined
};

export abstract class TableService<T> {
  // MC GPO-131 Versionado de API
  protected url = environment.apiUrl;
  protected recursos = this.recursosService.resource;
  // Private fields
  private _items$ = new BehaviorSubject<T[]>([]);
  private _items_ws$ = new BehaviorSubject<T[]>([]);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _isFirstLoading$ = new BehaviorSubject<boolean>(true);
  private _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
  private _errorMessage = new BehaviorSubject<string>('');
  private _subscriptions: Subscription[] = [];

  
  // Getters
  get items$() {
    return this._items$.asObservable();
  }
  get items_ws$() {
    return this._items_ws$.asObservable();
  }
  get isLoading$() {
    return this._isLoading$.asObservable();
  }
  get isFirstLoading$() {
    return this._isFirstLoading$.asObservable();
  }
  get errorMessage$() {
    return this._errorMessage.asObservable();
  }
  get subscriptions() {
    return this._subscriptions;
  }
  // State getters
  get paginator() {
    return this._tableState$.value.paginator;
  }
  get filter() {
    return this._tableState$.value.filter;
  }
  get sorting() {
    return this._tableState$.value.sorting;
  }
  get searchTerm() {
    return this._tableState$.value.searchTerm;
  }
  get grouping() {
    return this._tableState$.value.grouping;
  }

  // get tableState$() {
  //   return this._tableState$;
  // }

  protected http: HttpClient;
  protected auths:AuthService;
  // API URL has to be overrided
  API_URL = `${environment.apiUrl}/endpoint`;
  
  constructor(http: HttpClient, auths:AuthService = null, private recursosService: RecursosService) {
    this.http = http;
    this.auths = auths;
  }

  // CREATE
  // server should return the object with ID
  // create(item: BaseModel): Observable<BaseModel> {
  create(item: BaseModel): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.post<BaseModel>(this.API_URL, item).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('CREATE ITEM', err);
        // return of({ id: undefined, err: err });
        return of(err.error); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // READ (Returning filtered list of entities)
  find(tableState: ITableState): Observable<TableResponseModel<T>> {
    const url = this.API_URL + '/find';
    this._errorMessage.next('');
    return this.http.post<TableResponseModel<T>>(url, tableState).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('FIND ITEMS', err);
        return of({ items: [], items_ws: [], total: 0 });
      })
    );
  }

  getItemById(id: number): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.get<BaseModel>(url).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('GET ITEM BY IT', id, err);
        // return of({ id: undefined });
        return of({ id: undefined, error: err.error }); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE
  update(item: BaseModel): Observable<any> {
    const url = `${this.API_URL}/${item.id}`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.put(url, item).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('UPDATE ITEM', item, err);
        // return of(item);
        return of(err.error); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // MC GPO-127 PATCH
  patch(item: BaseModel): Observable<any> {
    const url = `${this.API_URL}/${item.id}`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.patch(url, item ).pipe(
      catchError(err => {
        this.isAuthenticationError(err);
        this._errorMessage.next(err);
        console.error('PATCH ITEM', item, err);
        // return of(item);
        return of(err.error); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE Status
  updateStatusForItems(ids: number[], status: number): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const body = { ids, status };
    const url = this.API_URL + '/updateStatus';
    return this.http.put(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('UPDATE STATUS FOR SELECTED ITEMS', ids, status, err);
        return of([]);
        // return of({items: [], err}); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // DELETE
  delete(id: any): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.delete(url).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('DELETE ITEM', id, err);
        // return of({});
        return of(err.error); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // delete list of items
  deleteItems(ids: number[] = []): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = this.API_URL + '/deleteItems';
    const body = { ids };
    return this.http.put(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err);
        console.error('DELETE SELECTED ITEMS', ids, err);
        return of([]);
        // return of({items: [], err}); // MC GPO-93
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  public fetch() {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.find(this._tableState$.value)
    // const request = this.find(this.tableState$.value)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.items);
          this._items_ws$.next(res.items_ws);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.total
            ),
            // paginator: this.tableState$.value.paginator.recalculatePaginator(
            //   res.total
            // ),
          });
        }),
        catchError((err) => {
          this._errorMessage.next(err);
          return of({
            items: [],
            // items_ws: [], // MC
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  // MC GPO-95 Método pensado para solicitar información sin que afecte a la paginación de otros servicios
  public fetchWithoutPaginator() {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.find(this._tableState$.value)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.items);
          this._items_ws$.next(res.items_ws);
          // this.patchStateWithoutFetch({});
          // this.patchState({});
          // this.patchStateWithoutFetch({
          //   paginator: this._tableState$.value.paginator.recalculatePaginator(
          //     0
          //   )
          // });
        }),
        catchError((err) => {
          this._errorMessage.next(err);
          return of({
            items: [],
            // items_ws: [], // MC
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public setDefaults() {
    this.patchStateWithoutFetch({ filter: {} });
    this.patchStateWithoutFetch({ sorting: new SortState() });
    this.patchStateWithoutFetch({ grouping: new GroupingState() });
    this.patchStateWithoutFetch({ searchTerm: '' });
    this.patchStateWithoutFetch({
      paginator: new PaginatorState()
    });
    this._isFirstLoading$.next(true);
    this._isLoading$.next(true);
    this._tableState$.next(DEFAULT_STATE);
    this._errorMessage.next('');
  }

  // Base Methods
  public patchState(patch: Partial<ITableState>) {
    this.patchStateWithoutFetch(patch);
    this.fetch();
  }

  public patchStateWithoutFetch(patch: Partial<ITableState>) {
    const newState = Object.assign(this._tableState$.value, patch);
    this._tableState$.next(newState);
  }

  public getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(`${environment.appVersion}-${environment.USERDATA_KEY}`)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  public logoutUser(){
    if (this.auths){
      this.auths.logout();
      document.location.reload(); 
    }
  }

  isAuthenticationError(err){
    if (err.error){
      if (err.status == 401){
        this.logoutUser();
      }
    }
  }

  public reiniciar_ws(){
    this._items_ws$ = undefined;
    this._items_ws$ = new BehaviorSubject<T[]>([]);
  }
}
